<template>
    <input
        :aria-disabled="disabled ? 'true' : 'false'"
        :checked="resolvedValue === true || undefined"
        class="form-checkbox bg-white rounded shadow-sm | transition-colors duration-100 | focus:outline-none focus:ring-0 focus:ring-offset-0 | focus-visible:ring-2 focus-visible:ring-offset-2"
        :class="[sizeClass, colorClass, cursorClass]"
        data-test="checkbox-input"
        :disabled="disabled"
        :indeterminate.prop="resolvedValue === null"
        :style="backgroundStyle"
        type="checkbox"
        @click.stop="toggle"
        @input.stop
        @change.stop
        @keyup.space.prevent="toggle"
    />
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { CheckboxColor, CheckboxSize, CheckboxType } from './types.ts';

    defineOptions({
        compatConfig: {
            MODE: 3,
            COMPONENT_V_MODEL: false,
        },
    });

    const MINUS_ICON_PATH = 'M18 12H6';
    const CHECK_ICON_PATH = 'M5 13l4 4L19 7';
    const CROSS_ICON_PATH = 'M6 18L18 6M6 6l12 12';

    const toSvgDataUrl = (path: string) =>
        `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='${path}' /%3E%3C/svg%3E")`;

    /**
     * Basic checkbox component.
     */

    interface Props {
        modelValue?: boolean | null;
        color?: CheckboxColor;
        type?: CheckboxType;
        tristate?: boolean;
        size?: CheckboxSize;
        disabled?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        modelValue: false,
        color: CheckboxColor.Blue,
        type: CheckboxType.Check,
        tristate: false,
        size: CheckboxSize.MD,
        disabled: false,
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean | null): void;
    }>();

    const resolvedValue = computed(() => {
        return props.modelValue;
    });

    // Normalize props to lowercase to accept e.g. both 'Blue' and 'blue'.
    const normalizedColor = computed(
        () => (props.color?.toLowerCase() ?? CheckboxColor.Blue) as CheckboxColor
    );
    const normalizedSize = computed(
        () => (props.size?.toLowerCase() ?? CheckboxSize.MD) as CheckboxSize
    );
    const normalizedType = computed(
        () => (props.type?.toLowerCase() ?? CheckboxType.Check) as CheckboxType
    );

    const colorClass = computed(() => {
        if (props.disabled) {
            return 'border-gray-200 bg-gray-100 text-gray-300';
        }

        const colorName = {
            [CheckboxColor.Green]: 'green',
            [CheckboxColor.Orange]: 'orange',
            [CheckboxColor.Red]: 'red',
            [CheckboxColor.Purple]: 'purple',
            [CheckboxColor.Blue]: 'blue',
        }[normalizedColor.value];

        if (resolvedValue.value === false) {
            return `border-gray-300 focus-visible:ring-${colorName}-500`;
        }

        return `border-${colorName}-500 text-${colorName}-500 focus-visible:ring-${colorName}-500`;
    });

    const sizeClass = computed(() => {
        return {
            [CheckboxSize.SM]: 'w-4 h-4',
            [CheckboxSize.MD]: 'w-5 h-5',
            [CheckboxSize.LG]: 'w-6 h-6',
            [CheckboxSize.XL]: 'w-8 h-8',
        }[normalizedSize.value];
    });

    const cursorClass = computed(() => {
        return props.disabled ? 'cursor-not-allowed' : 'cursor-pointer';
    });

    const backgroundStyle = computed(() => {
        if (resolvedValue.value === null) {
            return { backgroundImage: toSvgDataUrl(MINUS_ICON_PATH) };
        }

        if (resolvedValue.value === true) {
            const path =
                normalizedType.value === CheckboxType.Cross ? CROSS_ICON_PATH : CHECK_ICON_PATH;
            return { backgroundImage: toSvgDataUrl(path) };
        }

        return {};
    });

    const toggle = (event: Event) => {
        if (props.disabled) {
            event.preventDefault();
            return;
        }

        if (!props.tristate) {
            const next = !resolvedValue.value;
            emit('update:modelValue', next);
            return;
        }

        // Each click advances to the next state in the cycle:
        // null (indeterminate) → true (checked) → false (unchecked) → null ...
        // That's why each case emits the next state, not the current one.
        let next: boolean | null;

        switch (resolvedValue.value) {
            case null:
                next = true;
                break;
            case true:
                next = false;
                break;
            case false:
                next = null;
                break;
        }

        emit('update:modelValue', next);
    };
</script>
